import React from 'react';
import {
  IBigQueryConnectionInput,
  StrategyKind,
} from '../../../../utils/apollo/graphqlSchema';
import {
  InputField,
  TextAreaField,
  VerticalField,
} from '../../../../components/Form';
import { CDW_STRATEGY_OPTS, parseTimeout } from './utils';
import { WideSingleSelectMenu } from './DataModelerDataSourceInputUI';
import { DataModelerConfigLabelWithInfo } from '../DataModelerShared';
import { dataModelLabelInfoTooltipOptions } from '../../../../components/KubitTooltip/globalTooltips';

interface IProps {
  readOnly: boolean;
  canEditProps: boolean;
  bigQueryInput: IBigQueryConnectionInput;
  setBigQueryInput: (bigQueryInput: IBigQueryConnectionInput) => void;
  switchToStrategy: (newStrategy: StrategyKind) => void;
}

const DataModelerBigQueryConnectionInput = ({
  readOnly,
  canEditProps,
  bigQueryInput,
  setBigQueryInput,
  switchToStrategy,
}: IProps) => {
  const { account, dataset, projectId, computeProjectId, keyPlain, timeout } =
    bigQueryInput;

  const setStrategy = (kindStr: string) => {
    const kind = kindStr as StrategyKind;
    switchToStrategy(kind);
  };

  const setAccount = (newValue: string) => {
    setBigQueryInput({
      ...bigQueryInput,
      account: newValue,
    });
  };

  const setDataset = (newValue: string) => {
    setBigQueryInput({
      ...bigQueryInput,
      dataset: newValue,
    });
  };

  const setProjectId = (newValue: string) => {
    setBigQueryInput({
      ...bigQueryInput,
      projectId: newValue,
    });
  };

  const setComputeProjectId = (newValue: string) => {
    const newValueOrNull = newValue.trim() === '' ? null : newValue;
    setBigQueryInput({
      ...bigQueryInput,
      computeProjectId: newValueOrNull,
    });
  };

  const setKeyPlain = (newValue: string) => {
    setBigQueryInput({
      ...bigQueryInput,
      keyPlain: newValue,
    });
  };

  const setTimeoutSecStr = (newTimeoutStr: string) => {
    const newTimeout = parseTimeout(newTimeoutStr) ?? 600;
    setBigQueryInput({
      ...bigQueryInput,
      timeout: newTimeout,
    });
  };

  const keyPlainPlaceholder = canEditProps
    ? 'Please paste the contents of the BigQuery Service Account JSON file here'
    : 'Please paste the contents of the BigQuery Service Account JSON file here to make changes';

  return (
    <>
      <VerticalField noMargin={true}>
        <DataModelerConfigLabelWithInfo
          labelText={'Warehouse Type'}
          tooltipOption={
            dataModelLabelInfoTooltipOptions.connectionWarehouseType
          }
        />
        <WideSingleSelectMenu
          disabled={!canEditProps}
          value={StrategyKind.Bigquery}
          onChange={setStrategy}
          options={CDW_STRATEGY_OPTS}
        />
      </VerticalField>
      <VerticalField noMargin={true}>
        <DataModelerConfigLabelWithInfo
          labelText={'Account'}
          tooltipOption={dataModelLabelInfoTooltipOptions.connectionBqAccount}
        />
        <InputField
          type="text"
          value={account}
          onChange={setAccount}
          disabled={!canEditProps}
          placeholder={'Account'}
        />
      </VerticalField>
      <VerticalField noMargin={true}>
        <DataModelerConfigLabelWithInfo
          labelText={'Dataset'}
          tooltipOption={dataModelLabelInfoTooltipOptions.connectionBqDataset}
        />
        <InputField
          type="text"
          value={dataset}
          onChange={setDataset}
          disabled={!canEditProps}
          placeholder={'Dataset'}
        />
      </VerticalField>
      <VerticalField noMargin={true}>
        <DataModelerConfigLabelWithInfo
          labelText={'Project ID'}
          tooltipOption={dataModelLabelInfoTooltipOptions.connectionBqProjectId}
        />
        <InputField
          type="text"
          value={projectId}
          onChange={setProjectId}
          disabled={!canEditProps}
          placeholder={'Project ID'}
        />
      </VerticalField>
      <VerticalField noMargin={true}>
        <DataModelerConfigLabelWithInfo
          labelText={'Compute Project ID'}
          tooltipOption={
            dataModelLabelInfoTooltipOptions.connectionBqComputeProjectId
          }
        />
        <InputField
          type="text"
          value={computeProjectId ?? ''}
          onChange={setComputeProjectId}
          disabled={!canEditProps}
          placeholder={'Optional'}
        />
      </VerticalField>
      <VerticalField noMargin={true}>
        <DataModelerConfigLabelWithInfo
          labelText={'Query Timeout Seconds'}
          tooltipOption={dataModelLabelInfoTooltipOptions.connectionTimeout}
        />
        <InputField
          type="number"
          value={timeout ?? undefined}
          onChange={setTimeoutSecStr}
          disabled={!canEditProps}
          placeholder={'600'}
        />
      </VerticalField>
      <VerticalField noMargin={true}>
        <DataModelerConfigLabelWithInfo
          labelText={'JSON Key File Contents'}
          tooltipOption={dataModelLabelInfoTooltipOptions.connectionBqJsonKey}
        />
        <TextAreaField
          minRows={8}
          maxRows={8}
          disabled={readOnly}
          value={keyPlain}
          onChange={setKeyPlain}
          placeholder={keyPlainPlaceholder}
        />
      </VerticalField>
    </>
  );
};

export default DataModelerBigQueryConnectionInput;
